import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AppDispatch, RootState } from '../store';
import { getMyTravels } from '@cubemeet/core/store/slices/travelsSlice';
import { MODULE_COLORS } from '@cubemeet/core/theme';
import AddToItineraryModal from '../components/AddToItineraryModal';

type ShareIntentDraft = {
  id: string;
  title?: string;
  text?: string;
  url?: string;
  createdAt: number;
  source: 'share-target';
  travelId?: string | null;
};

const SHARE_INTENT_STORAGE_KEY = 'cm-share-intent';

const trimValue = (value: string | null): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const generateShareId = () => `share-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const TravelShareTargetPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);

  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const travelState = useSelector((state: RootState) => (state as any)?.travels ?? {}) as any;

  const user = authState?.user ?? null;
  const isAuthenticated = Boolean(authState?.isAuthenticated);
  const authLoading = Boolean(authState?.loading);

  const travels = useMemo(() => {
    const list = Array.isArray(travelState?.travels) ? travelState.travels : [];
    return list as Array<{ id: string; name?: string | null; location?: string | null; start_date?: string | null; end_date?: string | null }>;
  }, [travelState?.travels]);
  const travelsLoading = Boolean(travelState?.loading);

  const shareIdRef = useRef<string>(searchParams.get('shareId')?.trim() || generateShareId());
  const shareCreatedAtRef = useRef<number | null>(null);
  const lastShareKeyRef = useRef<string | null>(null);

  const rawTitleParam = searchParams.get('title') ?? searchParams.get('shareTitle');
  const rawTextParam = searchParams.get('text') ?? searchParams.get('shareText');
  const rawUrlParam = searchParams.get('url') ?? searchParams.get('shareUrl');
  const rawShareIdParam = searchParams.get('shareId');

  const shareDraft = useMemo<ShareIntentDraft | null>(() => {
    const title = trimValue(rawTitleParam);
    const text = trimValue(rawTextParam);
    const url = trimValue(rawUrlParam);

    if (!title && !text && !url) {
      lastShareKeyRef.current = null;
      shareCreatedAtRef.current = null;
      return null;
    }

    const key = JSON.stringify([title ?? null, text ?? null, url ?? null]);
    if (lastShareKeyRef.current !== key) {
      shareIdRef.current = rawShareIdParam?.trim() || generateShareId();
      shareCreatedAtRef.current = Date.now();
      lastShareKeyRef.current = key;
    }

    const createdAt = shareCreatedAtRef.current ?? Date.now();
    shareCreatedAtRef.current = createdAt;

    return {
      id: shareIdRef.current,
      title,
      text,
      url,
      createdAt,
      source: 'share-target',
      travelId: null,
    };
  }, [rawTitleParam, rawTextParam, rawUrlParam, rawShareIdParam]);

  useEffect(() => {
    if (!user?.id) return;
    dispatch(getMyTravels(user.id));
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!shareDraft) {
      try {
        window.sessionStorage.removeItem(SHARE_INTENT_STORAGE_KEY);
      } catch (err) {
        // ignore storage errors
      }
      return;
    }

    try {
      window.sessionStorage.setItem(SHARE_INTENT_STORAGE_KEY, JSON.stringify(shareDraft));
    } catch (err) {
      console.warn('TravelShareTargetPage: failed to persist share payload', err);
    }
  }, [shareDraft]);

  const writeShareIntent = useCallback(
    (travelId: string | null) => {
      if (!shareDraft) return null;
      const payload: ShareIntentDraft = {
        ...shareDraft,
        travelId,
        createdAt: Date.now(),
      };
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem(SHARE_INTENT_STORAGE_KEY, JSON.stringify(payload));
        } catch (err) {
          console.warn('TravelShareTargetPage: failed to persist share selection', err);
        }
      }
      return payload;
    },
    [shareDraft],
  );

  const buildShareQuery = useCallback((intent: ShareIntentDraft) => {
    const params = new URLSearchParams();
    params.set('fromShare', '1');
    if (intent.title) params.set('shareTitle', intent.title);
    if (intent.text) params.set('shareText', intent.text);
    if (intent.url) params.set('shareUrl', intent.url);
    if (intent.travelId) params.set('shareTravelId', intent.travelId);
    return params.toString();
  }, []);

  const handleOpenTravelList = useCallback(() => {
    const intent = writeShareIntent(null);
    if (!intent) return;
    const query = buildShareQuery(intent);
    navigate(`/travel?${query}`);
  }, [buildShareQuery, navigate, writeShareIntent]);

  const handleAttachToTravel = useCallback(
    (travelId: string) => {
      const intent = writeShareIntent(travelId);
      if (!intent) return;
      const query = buildShareQuery(intent);
      navigate(`/travel?${query}`);
    },
    [buildShareQuery, navigate, writeShareIntent],
  );

  const handleCreateTravel = useCallback(() => {
    const intent = writeShareIntent(null);
    if (!intent) return;
    navigate('/travel/create');
  }, [navigate, writeShareIntent]);

  const handleLogin = useCallback(() => {
    const pathWithQuery = typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/share/travel';
    navigate(`/auth?referer=${encodeURIComponent(pathWithQuery)}`);
  }, [navigate]);

  const handleOpenLinkPreview = useCallback(() => {
    if (!shareDraft?.url || typeof window === 'undefined') return;
    try {
      window.open(shareDraft.url, '_blank', 'noopener');
    } catch (err) {
      console.warn('TravelShareTargetPage: failed to open shared link', err);
    }
  }, [shareDraft?.url]);

  const handleOpenAddModal = useCallback(() => {
    setShowAddModal(true);
  }, []);

  const handleAdded = useCallback((entry: any) => {
    try {
      const gid = entry?.group_id || entry?.groupId;
      if (gid) navigate(`/trip/${gid}`);
    } catch {}
  }, [navigate]);

  return (
    <ScrollView style={styles.root} contentContainerStyle={styles.scrollContent}>
      <View style={styles.container}>
        <Text style={styles.pageTitle}>分享至行程表</Text>
        {shareDraft ? (
          <View style={styles.shareCard}>
            <Text style={styles.shareHeading}>已收到外部分享</Text>
            {shareDraft.title ? <Text style={styles.shareTitle}>{shareDraft.title}</Text> : null}
            {shareDraft.text ? <Text style={styles.shareText}>{shareDraft.text}</Text> : null}
            {shareDraft.url ? (
              <TouchableOpacity
                onPress={handleOpenLinkPreview}
                style={styles.shareLinkButton}
                activeOpacity={0.8}
              >
                <Text style={styles.shareLinkText}>预览分享链接</Text>
              </TouchableOpacity>
            ) : null}
            <View style={styles.shareOptions}>
              <TouchableOpacity
                style={styles.shareOptionPrimary}
                onPress={handleOpenAddModal}
                activeOpacity={0.9}
              >
                <Text style={styles.shareOptionPrimaryText}>添加到行程</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.shareOptionSecondary}
                onPress={handleCreateTravel}
                activeOpacity={0.9}
              >
                <Text style={styles.shareOptionSecondaryText}>创建新行程</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          <View style={styles.shareCardEmpty}>
            <Text style={styles.shareHeading}>未检测到分享内容</Text>
            <Text style={styles.shareHint}>请通过支持 Web Share 的应用再次分享，并确认选择“添加到行程表”。</Text>
          </View>
        )}

        {/* 行程选择旧列表已移除：改为统一使用底部模态 AddToItineraryModal 进行选行程/日期/时间 */}
        {shareDraft && !isAuthenticated ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>登录后即可保存到行程</Text>
            <View style={styles.authCard}>
              {authLoading ? (
                <ActivityIndicator size="small" color={MODULE_COLORS.travel} />
              ) : null}
              <Text style={styles.authHint}>请先登录 CubeMeet 账号，之后会自动返回此页面继续操作。</Text>
              <TouchableOpacity
                style={styles.authButton}
                onPress={handleLogin}
                activeOpacity={0.9}
              >
                <Text style={styles.authButtonText}>前往登录</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
      </View>
      {/* 统一复用“添加到行程”底部模态，传递外部分享预览 */}
      <AddToItineraryModal
        visible={Boolean(showAddModal && shareDraft)}
        onClose={() => setShowAddModal(false)}
        cardSnapshot={null}
        onAdded={handleAdded}
        externalShare={shareDraft ? { title: shareDraft.title, text: shareDraft.text, url: shareDraft.url, source: 'share-target' } : null}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingVertical: 32,
  },
  container: {
    flexGrow: 1,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 18,
  },
  shareCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    paddingVertical: 26,
    paddingHorizontal: 24,
    ...Platform.select({
      web: {
        boxShadow: '0px 16px 32px rgba(15,23,42,0.12)'
      },
      default: {
        shadowColor: '#0F172A',
        shadowOpacity: 0.12,
        shadowOffset: { width: 0, height: 16 },
        shadowRadius: 32,
        elevation: 12,
      },
    }),
    marginBottom: 28,
  },
  shareCardEmpty: {
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    paddingVertical: 26,
    paddingHorizontal: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: 28,
  },
  shareHeading: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
  },
  shareTitle: {
    marginTop: 10,
    fontSize: 15,
    fontWeight: '600',
    color: '#0E7490',
  },
  shareText: {
    marginTop: 12,
    fontSize: 14,
    lineHeight: 21,
    color: '#4B5563',
  },
  shareHint: {
    marginTop: 12,
    fontSize: 14,
    lineHeight: 21,
    color: '#6B7280',
  },
  shareLinkButton: {
    marginTop: 16,
    alignSelf: 'flex-start',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: '#E0F2FE',
  },
  shareLinkText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#0369A1',
  },
  shareOptions: {
    marginTop: 22,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  shareOptionPrimary: {
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 999,
    backgroundColor: MODULE_COLORS.travel,
    marginRight: 12,
    marginBottom: 10,
  },
  shareOptionPrimaryText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  shareOptionSecondary: {
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: MODULE_COLORS.travel,
    marginBottom: 10,
  },
  shareOptionSecondaryText: {
    color: MODULE_COLORS.travel,
    fontSize: 14,
    fontWeight: '600',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 16,
  },
  loadingBox: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    paddingVertical: 24,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 13,
    color: '#4B5563',
  },
  emptyList: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    paddingVertical: 24,
    paddingHorizontal: 24,
  },
  emptyListTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#111827',
  },
  emptyListHint: {
    marginTop: 8,
    fontSize: 13,
    lineHeight: 20,
    color: '#4B5563',
  },
  travelList: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  travelItem: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  travelItemBody: {
    flex: 1,
    paddingRight: 16,
  },
  travelItemTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0F172A',
  },
  travelItemSubtitle: {
    marginTop: 6,
    fontSize: 13,
    color: '#0369A1',
  },
  travelItemDates: {
    marginTop: 4,
    fontSize: 12,
    color: '#6B7280',
  },
  travelItemCta: {
    fontSize: 14,
    fontWeight: '600',
    color: MODULE_COLORS.travel,
  },
  authCard: {
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#FFFFFF',
    paddingVertical: 24,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  authHint: {
    marginTop: 12,
    fontSize: 13,
    lineHeight: 20,
    color: '#4B5563',
    textAlign: 'center',
  },
  authButton: {
    marginTop: 18,
    paddingVertical: 10,
    paddingHorizontal: 32,
    borderRadius: 999,
    backgroundColor: MODULE_COLORS.travel,
  },
  authButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
});

export default TravelShareTargetPage;
